<template>
  <div v-if="question">
    <v-card rounded="0" width="100%" elevation="0" class="word-break form-question">
      <div v-for="(answer, i) in answers" :key="answer.uuid" class="pb-2">
        <div class="d-flex">
          <div class="full-width">
            <AdditionalClaimantsInput
              v-if="question.type == 'additional-claimants'"
              :value="answer.selectedUsers"
              @input="answer.selectedUsers = $event"
              :label="Label"
              :has-error="!!hasFieldError(i)"
              :additional-claimant-rule="AdditionalClaimantRule"
              :additional-claimant-user-attributes="AdditionalClaimantUserAttributes"
              @selection-change="onAdditionalClaimantChange(i, $event)"
            />
            <v-textarea
              v-else-if="question.type == 'text-area'"
              outlined
              :color="!!hasFieldError(i) ? 'error' : 'brandCyan'"
              :label="Label"
              v-model.lazy="answer.textValue"
              class="rounded form-text-field"
              :class="{ 'invalid-input': !!hasFieldError(i) }"
              hide-details
              autocomplete="null"
              @input="runRules($event, i)"
            ></v-textarea>
            <v-text-field
              v-else-if="
                question.type == 'text-field' ||
                question.type == 'integer' ||
                question.type == 'decimal'
              "
              outlined
              dense
              :type="question.type == 'integer' || question.type == 'decimal' ? 'number' : 'text'"
              :color="!!hasFieldError(i) ? 'error' : 'brandCyan'"
              :label="Label"
              v-model.lazy="answer.textValue"
              class="rounded form-text-field"
              :class="{ 'invalid-input': !!hasFieldError(i) }"
              hide-details
              autocomplete="null"
              @input="runRules($event, i)"
            ></v-text-field>
            <v-autocomplete
              v-else-if="IsDropdown"
              outlined
              dense
              :color="!!hasFieldError(i) ? 'error' : 'brandCyan'"
              class="rounded form-dropdown"
              :class="{ 'invalid-input': !!hasFieldError(i) }"
              :label="Label"
              returnObject
              item-text="label"
              item-value="formQuestionValueId"
              v-model="answer.formQuestionValueId"
              :items="FormQuestionValues"
              no-data-text="No options found"
              :disabled="loadingChildQuestion"
              hide-details
              autocomplete="null"
              @change="userInputChanged(i, $event)"
            ></v-autocomplete>
            <div v-else-if="IsRadio">
              <Typography variant="body-1">{{ Label }}</Typography>
              <v-radio-group
                v-model="answer.formQuestionValueId"
                row
                hide-details
                class="form-radio-group"
                @change="userInputChanged(i, $event)"
                ><v-radio
                  v-for="val in FormQuestionValues"
                  :key="val.formQuestionValueId"
                  :label="val.label"
                  :value="val.formQuestionValueId"
                  :color="!!hasFieldError(i) ? 'error' : 'brandCyan'"
                  class="mb-3"
                ></v-radio
              ></v-radio-group>
            </div>
            <div v-else-if="question.type === 'date'" class="date-container">
              <Typography variant="body-1">{{ Label }}</Typography>
              <DatePicker
                :value="answer.textValue"
                @input="
                  answer.textValue = $event;
                  runRules($event, i);
                "
                placeholder="MM/DD/YYYY"
                :formatter="momentFormat"
                :disabled-date="disabledDates"
                value-type="YYYY-MM-DD"
                type="date"
                class="date-picker"
                :class="{ 'invalid-input': !!hasFieldError(i) }"
              ></DatePicker>
            </div>
            <v-checkbox
              v-else-if="question.type == 'checkbox'"
              outlined
              :color="!!hasFieldError(i) ? 'error' : 'brandCyan'"
              :label="Label"
              v-model.lazy="answer.textValue"
              true-value="1"
              false-value="0"
              class="checkbox-field"
              :class="{ 'invalid-input': !!hasFieldError(i) }"
              hide-details
              @change="runRules($event, i)"
            ></v-checkbox>
          </div>
          <v-btn
            v-if="answers.length > 1"
            class="mt-1 ml-2 delete-answer-button"
            :class="{ 'mt-7': question.type === 'date' }"
            icon
            @click="deleteAnswer(i)"
            ><v-icon color="red">mdi-trash-can-outline</v-icon></v-btn
          >
        </div>
        <div class="error-text mb-0" :class="hasFieldError(i) ? 'pb-2' : 'hidden'">
          {{ getFieldError(i) || "Error" }}
        </div>
      </div>
      <div v-if="question.repeated" class="d-flex justify-end pb-3">
        <Button outlined @click="addAnswer" class="repeated-answer-button">+</Button>
      </div>
      <div class="d-flex justify-center pb-4" v-if="loadingChildQuestion">
        <v-progress-circular indeterminate color="brandCyan" size="25" width="3" />
      </div>
      <FormQuestion
        v-else-if="childQuestion"
        :key="childQuestion.formQuestionId"
        :question="childQuestion"
        :formId="formId"
        :allPrefilledAnswers="allPrefilledAnswers"
        :initialAnswer="allPrefilledAnswers && allPrefilledAnswers[childQuestion.formQuestionId]"
        :childQuestions="childQuestions.filter((x) => x.formQuestionId != question.formQuestionId)"
        :validationRules="
          rules.filter((x) => x.formQuestionId && x.formQuestionId != question.formQuestionId) || []
        "
        :apiErrors="apiErrors"
        @update-responses="updateAnswerArray($event, 0)"
      />
    </v-card>
  </div>
</template>

<script>
// Components
import Button from "@/components/generic/Button";
import Typography from "@/components/generic/Typography";
import FormQuestion from "@/components/forms/FormQuestion";
import AdditionalClaimantsInput from "@/components/forms/AdditionalClaimantsInput";

// Packages
import DatePicker from "vue2-datepicker";
import moment from "moment-timezone";
import "vue2-datepicker/index.css";
import { mapState } from "vuex";
import { v4 as uuid } from "uuid";
import { exists } from "@/shared_data/functions";

const emptyAnswer = (prefill = {}) => {
  return {
    date: null,
    textValue: "",
    selectedValue: "",
    formQuestionValueId: null,
    uuid: uuid(),
    selectedUsers: [],
    ...prefill,
  };
};
export default {
  name: "FormQuestion",
  props: {
    question: Object,
    formId: Number,
    initialAnswer: String,
    // Answers from CSV to pass down to child questions
    allPrefilledAnswers: Object,
    whistleToken: String,
    childQuestions: Array,
    validationRules: {
      type: Array,
      default: () => [],
    },
    apiErrors: {
      type: Array,
      default: () => [],
    },
  },
  components: { DatePicker, Button, Typography, FormQuestion, AdditionalClaimantsInput },
  data() {
    return {
      out: [],
      answers: [],
      childAnswers: [],
      childQuestion: null,
      loadingChildQuestion: false,
      childQuestionError: "",
      // Use moment.js instead of the default
      momentFormat: {
        // Formats the string to show
        stringify: (date) => {
          return date ? moment(date).format("MM/DD/YYYY") : "";
        },
        parse: (date) => {
          return date ? moment(date, "MM/DD/YYYY").toDate() : null;
        },
      },
      rules: [],
      answerErrors: [],
      frontendErrors: [],
      backendErrors: [],
    };
  },
  created() {
    this.rules = this.validationRules || [];
  },
  updated() {},
  mounted() {
    if (this.initialAnswer) {
      // Store the answer so we can overwrite for checkboxes
      let answerValue = this.initialAnswer;

      const formQuestionValue = this.FormQuestionValues.find(
        (x) => x.label && x.label.toLowerCase().trim() === answerValue.toLowerCase().trim()
      );

      // If they didn't have a match on the form question values, then we don't want to assign the text value
      // If the question has no form question values, then we can assume it's a text field
      if (!this.FormQuestionValues.length || !!formQuestionValue) {
        // Check for truthy values for checkboxes
        if (this.question.type === "checkbox") {
          const trimmedAnswer = this.initialAnswer.toLowerCase().trim();
          answerValue =
            trimmedAnswer === "1" ||
            trimmedAnswer === "true" ||
            trimmedAnswer === "yes" ||
            trimmedAnswer === "y"
              ? "1"
              : "0";
        }

        this.answers = [
          emptyAnswer({
            // The userInputChanged method expects a FormQuestionValue if an object, hence a label rather than textValue
            label: formQuestionValue ? formQuestionValue.label : answerValue,
            formQuestionValueId: formQuestionValue ? formQuestionValue.formQuestionValueId : null,
          }),
        ];
        // Check for childQuestionId on the formQuestionValue or the question itself
        const childQuestionId =
          (formQuestionValue && formQuestionValue.childQuestionId) || this.question.childQuestionId;

        if (childQuestionId) {
          this.childQuestion = this.childQuestions.find((x) => x.formQuestionId == childQuestionId);
        }
        this.userInputChanged(0, this.answers[0]);
      } else {
        // Prefill the text value so we can show the error message saying
        // an option is not valid for the dropdown
        this.answers = [emptyAnswer({ textValue: this.initialAnswer })];
        this.$nextTick(() => {
          this.answers.forEach((answer, index) => {
            if (answer.textValue) {
              this.runRules(answer.textValue, index);
            }
          });
        });
      }
    } else {
      this.answers.push(emptyAnswer());
    }

    // If a checkbox we default to false
    // if (this.question.type === "checkbox") this.answers[0].textValue = "0";
  },
  beforeMount() {},
  methods: {
    userInputChanged(index, e) {
      let answer = e;
      // Always look up the actual FormQuestionValue to get the childQuestionId
      if (typeof e !== "object") {
        // e is the ID of the form question value
        answer = this.FormQuestionValues.find((x) => e === x.formQuestionValueId);
      } else if (e.formQuestionValueId) {
        // e is an answer object - look up the actual FormQuestionValue
        answer =
          this.FormQuestionValues.find((x) => e.formQuestionValueId === x.formQuestionValueId) || e;
      }

      if (!answer) {
        console.error("No answer found in userInputChanged", e);
        return;
      }

      this.answers[index].formQuestionValueId = answer.formQuestionValueId;
      this.answers[index].textValue = answer.label;

      // Run rules for given input
      this.runRules(answer.label, index);

      const childQuestionId = answer.childQuestionId || this.question.childQuestionId;

      if (childQuestionId) {
        this.childQuestion = this.childQuestions.find((x) => x.formQuestionId == childQuestionId);
      } else {
        // Wipe values so if there's a child question, we no longer show it
        this.childQuestion = null;
        // Wipes the child's answer from the answer array
        this.updateAnswerArray(undefined, 0);
      }

      this.clearBackendError(index);
    },
    updateAnswerArray(e) {
      // With the repeated field, e is going to be an array that we want to store in our answer array at index i
      this.childAnswers = e;
    },
    formatOutput(currentQuestion, children = []) {
      // Children is an array of arrays, so we need to go through each
      let formattedChildren = (children || []).filter(Boolean).filter((a) => a.length);
      let res = [];
      (currentQuestion || []).filter(Boolean).forEach((output) => {
        if (!formattedChildren.length) return res.push([output]);

        formattedChildren.filter(Boolean).forEach((child) => {
          // An array of child answers combined with each of this current question's output
          res.push([output, child].flat(Infinity).filter(Boolean));
        });
      });

      // console.log("format output after" + this.question.formQuestionId, res);
      return res;
    },
    addAnswer() {
      this.answers.push(emptyAnswer());
    },
    deleteAnswer(index) {
      this.answers.splice(index, 1);
      this.answerErrors.splice(index, 1);
      this.backendErrors.splice(index, 1);
      this.frontendErrors.splice(index, 1);
    },
    runRules(val, answerIndex) {
      console.log("Refreshing rules", val);
      const results = (this.FormRules || [])
        .map((rule) => rule(val, answerIndex))
        .filter((res) => res !== true);
      console.log("Rule results", results);
      if (results.length) {
        console.log("Logging error", results[0]);
        this.$set(this.frontendErrors, answerIndex, results[0]);
      } else this.$set(this.frontendErrors, answerIndex, null);

      this.clearBackendError(answerIndex);
    },
    onAdditionalClaimantChange(answerIndex, payload) {
      // Convert selected user objects into a string for submission.
      // Submit comma-separated user IDs instead of names
      const selected =
        (payload && payload.selectedUsers) ||
        (this.answers[answerIndex] && this.answers[answerIndex].selectedUsers) ||
        [];
      const selectedList = Array.isArray(selected) ? selected : [selected];
      const idList =
        (payload && payload.idList) ||
        selectedList
          .filter(Boolean)
          .map((u) => (u && typeof u === "object" ? u.userId : u))
          .filter((id) => id !== undefined && id !== null && id !== "")
          .join(",");
      if (this.answers[answerIndex]) this.answers[answerIndex].textValue = idList || "";

      this.runRules(idList || "", answerIndex);
    },
    validateRegExp(v, exp) {
      let valid = !!v;
      const regex = new RegExp(exp);
      valid = regex.test(v);

      console.log("Regex", { valid, v, logic: !v && !this.question.required });
      return valid || (!v && !this.question.required);
    },
    validateLength(v, min, max) {
      let valid = !!v;
      let len = v.length || 0;

      // Default value is true in case min/max is null (which means we don't test)
      let validMin = true;
      let validMax = true;
      if (min !== undefined && min !== null) {
        validMin = len >= min;
      }

      if (max !== undefined && max !== null) {
        validMax = len <= max;
      }

      valid = validMin && validMax;
      // console.log("Validate length", { v, min, max, valid });

      return valid || (!v && !this.question.required);
    },
    validateNumber(v, min, max) {
      let valid = !!v;
      let val = Number(v || 0);

      // Default value is true in case min/max is null (which means we don't test)
      let validMin = true;
      let validMax = true;
      if (min !== undefined && min !== null) {
        validMin = val >= min;
      }

      if (max !== undefined && max !== null) {
        validMax = val <= max;
      }

      valid = validMin && validMax;
      // console.log("Validate length", { v, min, max, valid });

      return valid || (!v && !this.question.required);
    },
    validateDate(v, dateFormat, minRule, maxRule, numDaysInPast, numDaysInFuture) {
      let valid = !!v;
      try {
        let min = minRule && moment(minRule);
        let max = maxRule && moment(maxRule);

        const timezone = moment.tz.guess();

        let enteredDate = moment(v, dateFormat);

        // Default value is true in case min/max is null (which means we don't test)
        let validMin = true;
        let validMax = true;
        if (min !== undefined && min !== null) {
          validMin = enteredDate.isSameOrAfter(min);
        }

        if (max !== undefined && max !== null) {
          validMax = enteredDate.isSameOrBefore(max);
        }

        // We only check numDaysInPast/numDaysInFuture if the min/max rules are not set
        if (!exists(min) && !exists(max)) {
          // Convert date to the timezone of the form so we compare like dates
          enteredDate = enteredDate.tz(timezone).startOf("day");
          console.log("Checking numDaysInPast/numDaysInFuture", {
            numDaysInPast,
            numDaysInFuture,
            timezone: timezone,
          });
          if (numDaysInPast !== undefined && numDaysInPast !== null) {
            validMin = enteredDate.isSameOrAfter(
              moment().startOf("day").tz(timezone).subtract(numDaysInPast, "days")
            );
          }
          if (numDaysInFuture !== undefined && numDaysInFuture !== null) {
            validMax = enteredDate.isSameOrBefore(
              moment().startOf("day").tz(timezone).add(numDaysInFuture, "days")
            );
          }
        }

        valid = validMin && validMax;
      } catch (e) {
        // If we have an error we mark as invalid since a date somewhere is messed up
        console.log("Error parsing date validity", e);
        valid = false;
      }

      return valid || (!v && !this.question.required);
    },
    // Check if a field has any error (frontend or backend)
    hasFieldError(index) {
      return !!(this.frontendErrors[index] || this.backendErrors[index]);
    },

    // Get the error message for a field (prioritize frontend errors)
    getFieldError(index) {
      return this.frontendErrors[index] || this.backendErrors[index];
    },

    // Clear backend error when user starts typing
    clearBackendError(index) {
      if (this.backendErrors[index]) {
        // We used to splice the error but we need to keep the index in sync with the answers array
        this.backendErrors[index] = undefined;
      }
    },

    // Clear all backend errors
    clearAllBackendErrors() {
      this.backendErrors = [];
    },
    // Disable dates in the calendar based on date validation rules
    disabledDates(date) {
      // Get any validation rules with numDaysInPast/numDaysInFuture or min/max
      const dateRules = this.ValidationRules.filter(
        (r) =>
          // Optimize our check by filtering out any non-date rules
          exists(r.dateConstraints) &&
          (exists(r.dateConstraints.numDaysInPast) ||
            exists(r.dateConstraints.numDaysInFuture) ||
            exists(r.dateConstraints.min) ||
            exists(r.dateConstraints.max))
      );

      if (!dateRules.length) return false;

      const timezone = moment.tz.guess();
      const momentDate = moment(date).tz(timezone);
      const result = dateRules.some((rule) => {
        return !this.validateDate(
          momentDate,
          undefined,
          rule.dateConstraints.min,
          rule.dateConstraints.max,
          rule.dateConstraints.numDaysInPast,
          rule.dateConstraints.numDaysInFuture
        );
      });

      return result;
    },
  },
  computed: {
    ...mapState(["userProfile"]),
    // New rule for additionalClaimantConstraints
    AdditionalClaimantRule() {
      return (this.ValidationRules || []).find((r) => r && r.additionalClaimantConstraints);
    },
    AdditionalClaimantUserAttributes() {
      const constraints =
        this.AdditionalClaimantRule && this.AdditionalClaimantRule.additionalClaimantConstraints;
      const empty = { min: undefined, max: undefined };
      if (!constraints) return empty;

      // Format to valid arrays
      const normalizeToArray = (v) => {
        if (v === undefined || v === null) return [];
        if (Array.isArray(v)) return v.filter(Boolean);
        if (typeof v === "string" && v.trim()) return [v.trim()];
        return [];
      };

      const normalizeToNumber = (v) => {
        if (v === undefined || v === null || v === "") return undefined;
        const n = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(n)) return undefined;
        return n;
      };

      // Constraint format:
      // additionalClaimantConstraints.userAttributes = { <attributeKey>: [...] }
      const raw = constraints.userAttributes;
      if (!raw) return empty;

      let parsed = raw;
      if (typeof parsed === "object" && parsed !== null && "value" in parsed) {
        parsed = parsed.value;
      }
      if (typeof parsed === "string") {
        try {
          parsed = JSON.parse(parsed);
        } catch (e) {
          parsed = {};
        }
      }
      if (typeof parsed !== "object" || parsed === null) parsed = {};

      const normalizedAttributes = Object.keys(parsed || {}).reduce((acc, key) => {
        acc[key] = normalizeToArray(parsed[key]);
        return acc;
      }, {});

      return {
        ...normalizedAttributes,
        min: normalizeToNumber(constraints.min),
        max: normalizeToNumber(constraints.max),
      };
    },
    FormQuestionValues() {
      return (this.question && this.question.FormQuestionValues) || [];
    },
    Label() {
      if (!this.question) return "";
      return this.question.label + (this.question.required ? " *" : "");
    },
    IsDropdown() {
      return ["dropdown", "project", "category", "subcategory"].includes(this.question.type);
    },
    IsRadio() {
      return this.question.type === "radio";
    },
    ValidationRules() {
      return this.rules.filter((r) => r.formQuestionId === this.question.formQuestionId) || [];
    },
    FormRules() {
      let arr = [];

      if (!this.question) return arr;

      // If additionalClaimantConstraints applies, the user must select a value from the autocomplete
      if (this.question.type === "additional-claimants") {
        arr.push((v, answerIndex = 0) => {
          const selectedCount =
            this.answers && this.answers[answerIndex] && this.answers[answerIndex].selectedUsers
              ? this.answers[answerIndex].selectedUsers.length
              : 0;

          const attrs = this.AdditionalClaimantUserAttributes || {};

          console.log("AdditionalClaimantUserAttributes", attrs);
          const min = Number.isFinite(attrs.min)
            ? attrs.min
            : this.question && this.question.required
            ? 1
            : 0;
          const max = Number.isFinite(attrs.max) ? attrs.max : undefined;

          if (Number.isFinite(max) && selectedCount > max) {
            return `Only ${max} number of users is allowed`;
          }

          if (Number.isFinite(min) && selectedCount < min) {
            return `Select at least ${min} name${min === 1 ? "" : "s"}`;
          }

          return true;
        });
      }

      if (this.IsDropdown || this.IsRadio) {
        arr.push(
          (v) =>
            this.FormQuestionValues.some((x) => x.label === v) ||
            `Invalid option${
              this.allPrefilledAnswers && this.allPrefilledAnswers.__row
                ? ` for row ${this.allPrefilledAnswers.__row}`
                : ""
            }: ${v}`
        );
      }
      if (this.question.required)
        arr.push((v, answerIndex = 0) => {
          // Checkboxes require a 'truthy' input to continue
          if (this.question.type === "checkbox") {
            return v === "1" || "Input is required";
          }

          if (this.question.type === "additional-claimants") {
            const selectedCount =
              this.answers && this.answers[answerIndex] && this.answers[answerIndex].selectedUsers
                ? this.answers[answerIndex].selectedUsers.length
                : 0;
            return selectedCount > 0 || "Input is required";
          }

          if (Array.isArray(v)) {
            return v.length > 0 || "Input is required";
          }

          return !!v || "Input is required";
        });
      if (this.question.type === "integer")
        arr.push(
          (v) =>
            v === undefined ||
            v === null ||
            v === "" ||
            (v && Number.isInteger(Number(v))) ||
            "Input should be a whole number"
        );
      if (this.question.type === "decimal")
        arr.push(
          (v) =>
            v === undefined ||
            v === null ||
            v === "" ||
            !!(
              v &&
              (!v.toString().includes(".") ||
                (v.toString().includes(".") &&
                  v.toString().substring(v.toString().indexOf(".") + 1).length < 3))
            ) ||
            `Input shouldn't be longer than 2 decimals.`
        );
      this.ValidationRules.forEach((rule) => {
        // Only process the rules that apply to frontend
        if (
          rule.regexConstraint ||
          rule.lengthConstraints ||
          rule.dateConstraints ||
          rule.numberConstraints
        ) {
          arr.push((v) => {
            let valid;
            if (rule.regexConstraint) valid = this.validateRegExp(v, rule.regexConstraint);
            else if (rule.lengthConstraints)
              valid = this.validateLength(
                v,
                rule.lengthConstraints.min,
                rule.lengthConstraints.max
              );
            else if (rule.dateConstraints)
              valid = this.validateDate(
                v,
                "MM/DD/YYYY",
                rule.dateConstraints.min,
                rule.dateConstraints.max,
                rule.dateConstraints.numDaysInPast,
                rule.dateConstraints.numDaysInFuture
              );
            else if (rule.numberConstraints)
              valid = this.validateNumber(
                v,
                rule.numberConstraints.min,
                rule.numberConstraints.max
              );

            return !!valid || rule.errorMessage || "Invalid entry";
          });
        }
      });

      return arr;
    },
    // Formats FormSubmissionEntryValue object
    Output() {
      // Supports an array if repeated
      let arr = this.answers.map((answer, i) => {
        let textValue = answer.textValue;
        if (this.question && this.question.type === "additional-claimants") {
          const selectedUsers = Array.isArray(answer.selectedUsers) ? answer.selectedUsers : [];
          if (selectedUsers.length) {
            textValue = selectedUsers
              .filter(Boolean)
              .map((u) => (u && typeof u === "object" ? u.userId : u))
              .filter((id) => id !== undefined && id !== null && id !== "")
              .join(",");
          }
        }
        const required = !!(this.question && this.question.required);
        // Initialized to match if textValue has a value. That way we don't say something's valid when it has no value
        // The only time valid is true at the same time is if it's not required
        let valid =
          (this.frontendErrors[i] === undefined || this.frontendErrors[i] === null) &&
          (!!textValue || !required);

        if (this.question && this.question.type === "date" && textValue)
          textValue = moment(textValue, "MM/DD/YYYY").format("YYYY-MM-DD");

        return {
          uuid: answer.uuid,
          textValue: textValue === "" ? null : textValue,
          formQuestionId: this.question && this.question.formQuestionId,
          formQuestionValueId: answer.formQuestionValueId,
          required,
          validated: !!valid,
          // add additionalClaimants selected users
        };
      });
      return arr;
    },
  },
  watch: {
    childAnswers: {
      deep: true,
      handler(n) {
        const out = this.formatOutput(this.Output, n);
        this.out = out;
        this.$emit("update-responses", out);
      },
    },
    Output: function (n) {
      const out = this.formatOutput(n, this.childAnswers);
      this.out = out;
      this.$emit("update-responses", out);
    },
    apiErrors: {
      handler(n) {
        // If the API errors change, we need to refresh the error messages
        let questionErrors = (n || []).filter(
          (r) => r.formQuestionId === this.question.formQuestionId
        );

        // Now match on uuid
        this.answers.forEach((answer, i) => {
          // Find the error for this answer
          let error = questionErrors.find((e) => e.uuid === answer.uuid);

          if (error) {
            // If we have an error, we set it
            this.$set(this.backendErrors, i, error.error || "Invalid entry");
          } else {
            // Otherwise, we clear the error
            this.$set(this.backendErrors, i, null);
          }
        });
      },
      deep: true,
      // immediate: false
    },
  },
};
</script>

<style scoped>
.mx-datepicker {
  width: 100%;
}
</style>
<!-- CSS for date picker so it's not scoped -->
<style>
.date-picker .mx-input {
  /* Matches Vuetify border color when inactive */
  border-color: #9e9e9e !important;
  height: 40px !important;
  /* border: 1px solid !important; */
}
.date-picker .mx-input:hover {
  /* Matches Vuetify - black when hovering */
  border-color: black !important;
}
.date-picker .mx-input:focus {
  border: 2px solid !important;
  border-color: var(--v-brandCyan-base) !important;
}

/* Invalid date picker */
.date-picker.invalid-input .mx-input {
  border: 2px solid !important;
  border-color: var(--v-error-base) !important;
}
.date-picker.invalid-input .mx-input:hover {
  border-color: var(--v-error-base) !important;
}
.date-picker.invalid-input .mx-input:focus {
  border-color: var(--v-error-base) !important;
}

/* Invalid text box */
.form-text-field.invalid-input fieldset {
  border: 2px solid !important;
  border-color: var(--v-error-base) !important;
}
.form-text-field.invalid-input label {
  color: var(--v-error-base) !important;
}

.checkbox-field {
  margin-top: 0;
}
.checkbox-field.invalid-input fieldset {
  border: 2px solid !important;
  border-color: var(--v-error-base) !important;
}
.checkbox-field.invalid-input label {
  color: var(--v-error-base) !important;
}
.checkbox-field.invalid-input i {
  color: var(--v-error-base) !important;
}

/* Invalid dropdown */
.form-dropdown.invalid-input fieldset {
  border: 2px solid !important;
  border-color: var(--v-error-base) !important;
}
.form-dropdown.invalid-input label {
  color: var(--v-error-base) !important;
}

/* Generic form validation error text shown for any question */
.error-text {
  color: var(--v-error-base);
  margin-top: 6px;
  margin-left: 8px;
  /* font-weight: 700; */
  line-height: 12px;
  font-size: 12px;
}
</style>
